import React, { useEffect, useMemo, useState } from 'react';

import { Image } from 'antd';

import { getVideoCover, isVideo } from '@libs/NwUtils';

import { forceReloadPackagePlaylist } from '@hooks/API/Packages/UsePackagePlaylist';
import { forceReloadPackage } from '@hooks/API/Packages/UsePackages';

import { Loading } from '@components/Gui/Loading';
import { NwButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import NwSegmented from '@components/Gui/NwForm/NwSegmented';
import PlayListInfoForm from '@components/MediaDrawer/PlayListInfoForm';
import ModalVideo from '@components/PlaylistEditor/ModalVideo';
import { usePlaylistEditor, usePlaylistEditorDispatch } from '@components/PlaylistEditor/PlaylistEditorContext';
import { isPlaceholder } from '@components/PlaylistEditor/PlaylistEditorUtils';
import usePlaylistEditorData from '@components/PlaylistEditor/usePlaylistEditorData';
import useSaveBook from '@components/PlaylistEditor/useSaveBook';

import MobileBookCanvas from './MobileBookCanvas';
import { MobileBookEditorWrapper, PICKER_BACKGROUND } from './MobileBookEditorUI';
import MobileMediaPicker from './MobileMediaPicker';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/** Le etichette seguono cio' che l'API salva in `Layout`. */
const LAYOUT_OPTIONS = [
    { label: 'Spreads', value: false },
    { label: 'Sequence', value: true },
];

const EMPTY_PLAYLIST_INFO = {
    Name: '',
    Description: '',
    IsPublished: false,
    PrivacyLevelName: 'Public',
};

/**
 * Editor del book per smartphone: due modalita' invece dei due pannelli del
 * desktop. Prima si selezionano i media nello sheet d'archivio, poi si dispone
 * il book. Logica di dominio, salvataggio e caricamento sono quelli condivisi
 * con `@components/PlaylistEditor`.
 */
const MobileBookEditor = ({ onRequestClose }) => {
    const { bookToEdit, bookToEditData, onClose, packageID, playlistItems, previewVideo, singlePage } =
        usePlaylistEditor();
    const dispatch = usePlaylistEditorDispatch();

    usePlaylistEditorData();
    const { saveBook, isSaving } = useSaveBook();

    const [selectedID, setSelectedID] = useState(null);
    const [movingID, setMovingID] = useState(null);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [touchForm, setTouchForm] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [playlistInfo, setPlaylistInfo] = useState(EMPTY_PLAYLIST_INFO);

    useEffect(() => {
        if (bookToEditData) {
            setPlaylistInfo({
                Name: bookToEditData.Name || '',
                Description: bookToEditData.Description || '',
                IsPublished: bookToEditData.IsPublished || false,
                PrivacyLevelName: bookToEditData.PrivacyLevelName || 'Public',
            });
        }
    }, [bookToEditData]);

    const counts = useMemo(() => {
        const mediaCount = playlistItems.filter((item) => !isPlaceholder(item)).length;
        const parts = [`${mediaCount} ${mediaCount === 1 ? 'item' : 'items'}`];
        if (singlePage) {
            parts.push('in sequence');
        } else {
            const lastPage = playlistItems.length ? playlistItems[playlistItems.length - 1].page : 0;
            parts.push(`${Math.ceil(lastPage / 2)} spreads`);
        }
        return parts.join(' · ');
    }, [playlistItems, singlePage]);

    const movingItem = useMemo(
        () => playlistItems.find((item) => item.ID === movingID),
        [playlistItems, movingID]
    );

    const handleSelect = (playlistItem) => {
        setSelectedID((current) => (current === playlistItem.ID ? null : playlistItem.ID));
    };

    const handleMove = (playlistItem) => {
        setSelectedID(null);
        setMovingID(playlistItem.ID);
    };

    const handlePlace = (targetIndex) => {
        const oldIndex = playlistItems.findIndex((item) => item.ID === movingID);
        setMovingID(null);
        if (oldIndex === -1) {
            return;
        }
        // il binario indica una posizione tra due item: dopo aver tolto l'item
        // spostato, gli indici successivi scalano di uno
        const newIndex = targetIndex > oldIndex ? targetIndex - 1 : targetIndex;
        if (newIndex !== oldIndex) {
            dispatch({ type: 'MovePlaylistItem', value: { oldIndex, newIndex } });
        }
    };

    const handleRemove = (playlistItem) => {
        setSelectedID(null);
        dispatch({ type: 'RemovePlaylistItem', value: playlistItem.ID });
    };

    const handlePreview = (playlistItem) => {
        setSelectedID(null);
        const media = playlistItem.media;
        if (isVideo(media)) {
            dispatch({ type: 'SetPreviewVideo', value: media });
        } else {
            setPreviewImage(media.HD ? media.HD.Url : media.Standard.Url);
        }
    };

    const handleLayoutChange = (value) => {
        setSelectedID(null);
        setMovingID(null);
        dispatch({ type: 'ToggleSinglePage', value });
    };

    const handleSave = async () => {
        const saved = await saveBook();
        if (saved) {
            onClose();
        }
    };

    if (!bookToEditData) {
        return <Loading textBlack small paddingTop="3rem" />;
    }

    return (
        <>
            <MobileBookEditorWrapper>
                <div className="mobile-book-topbar">
                    <div className="topbar-title">
                        <NwButton
                            ghost
                            iconOnly
                            icon={byPrefixAndName.fasl['chevron-left']}
                            aria-label="Close the book editor"
                            onClick={onRequestClose}
                        />
                        <h2>{playlistInfo.Name}</h2>
                        <NwButton
                            ghost
                            iconOnly
                            icon={byPrefixAndName.fasl['pen']}
                            aria-label="Rename book"
                            onClick={() => setIsRenaming(true)}
                        />
                    </div>
                    <div className="topbar-meta">
                        <span className="meta-counts">{counts}</span>
                        <NwSegmented
                            size="small"
                            options={LAYOUT_OPTIONS}
                            value={singlePage}
                            onChange={handleLayoutChange}
                        />
                    </div>
                </div>

                <div className="mobile-book-canvas" onClick={() => setSelectedID(null)}>
                    {playlistItems.length === 0 ? (
                        <div className="mobile-book-empty">
                            <h3>This book is empty</h3>
                            <p>Add photos and videos from the model’s archive to start.</p>
                        </div>
                    ) : (
                        <MobileBookCanvas
                            movingID={movingID}
                            selectedID={selectedID}
                            onSelect={handleSelect}
                            onMove={handleMove}
                            onPlace={handlePlace}
                            onPreview={handlePreview}
                            onRemove={handleRemove}
                        />
                    )}
                </div>

                {movingID ? (
                    <div className="mobile-book-movebar">
                        <span className="movebar-thumb">
                            {movingItem && movingItem.media && (
                                <img
                                    src={
                                        isVideo(movingItem.media)
                                            ? getVideoCover(movingItem.media)
                                            : movingItem.media.Thumbnail && movingItem.media.Thumbnail.Url
                                    }
                                    alt=""
                                />
                            )}
                        </span>
                        <span className="movebar-text">
                            <b>{`Moving ${movingItem && !movingItem.media ? 'blank ' : ''}page ${movingItem ? movingItem.page : ''}`}</b>
                            <span>Tap a blue rail to place</span>
                        </span>
                        <button type="button" className="movebar-cancel" onClick={() => setMovingID(null)}>
                            Cancel
                        </button>
                    </div>
                ) : (
                    <div className="mobile-book-actionbar">
                        <NwButton
                            icon={byPrefixAndName.fasl['plus']}
                            label="Add media"
                            onClick={() => setIsPickerOpen(true)}
                        />
                        <NwSaveButton
                            primary
                            htmlType="button"
                            label="Save book"
                            loading={isSaving}
                            onClick={handleSave}
                        />
                    </div>
                )}
            </MobileBookEditorWrapper>

            {isPickerOpen && (
                <NwDrawer
                    fixed
                    title={null}
                    padding={0}
                    bgColor={PICKER_BACKGROUND}
                    zIndex={1050}
                    onClose={() => setIsPickerOpen(false)}
                >
                    <MobileMediaPicker onClose={() => setIsPickerOpen(false)} onPreviewImage={setPreviewImage} />
                </NwDrawer>
            )}

            {isRenaming && (
                <NwDrawer
                    touchForm={touchForm}
                    onTouchForm={setTouchForm}
                    title="Edit book"
                    zIndex={1050}
                    onClose={() => setIsRenaming(false)}
                >
                    <PlayListInfoForm
                        playlist={{ ...bookToEditData, ...playlistInfo }}
                        packageID={packageID}
                        onUpdatePlaylists={(updatedPlaylist) => {
                            setPlaylistInfo({
                                Name: updatedPlaylist.Name || '',
                                Description: updatedPlaylist.Description || '',
                                IsPublished: updatedPlaylist.IsPublished || false,
                                PrivacyLevelName: updatedPlaylist.PrivacyLevel || 'Public',
                            });
                            if (packageID) {
                                forceReloadPackagePlaylist(bookToEdit);
                                forceReloadPackage(packageID);
                            }
                        }}
                        onTouchForm={setTouchForm}
                        onClose={() => setIsRenaming(false)}
                    />
                </NwDrawer>
            )}

            {previewImage && (
                <Image
                    wrapperStyle={{ display: 'none' }}
                    src={previewImage}
                    preview={{
                        visible: true,
                        src: previewImage,
                        onVisibleChange: (visible) => {
                            if (!visible) {
                                setPreviewImage(null);
                            }
                        },
                    }}
                />
            )}

            {previewVideo && (
                <ModalVideo video={previewVideo} onClose={() => dispatch({ type: 'SetPreviewVideo', value: null })} />
            )}
        </>
    );
};

export default MobileBookEditor;
